import PropTypes from 'prop-types';
import React from 'react';
import { List } from 'immutable';

import { translate as t } from '../../lib/I18n';
import OrderFare from '../../records/order/Fare';
import OrderTrip from '../../records/order/Trip';
import { getFoldedTickets } from '../../helpers/Fares';
import connectToStores from '../../helpers/connectToStores';
import LinkingSmartcardsStore from '../../stores/LinkingSmartcardsStore';
import FeatureFlagStore from '../../stores/FeatureFlagStore';
import PreviousSearchesStore from '../../stores/PreviousSearchesStore';
import PassengerAssistStore from '../../stores/PassengerAssistStore';

import FareJourneys from './FareJourneys';
import FareRestrictions from './FareRestrictions';
import FareSummary from './FareSummary';
import FarePassengers from './FarePassengers';
import Validity from '../fares/details/Validity';
import LinkedSmartcards from '../smartcard/LinkedSmartcards';
import RewardPointsEarned from './orderSummary/RewardPointsEarned';
import FeatureFlag from '../general/FeatureFlag';
import Icon from '../general/Icon';


const getStateFromStores = ({ orderFare }) => {
    return {
        enableSmartcardAssociations: FeatureFlagStore.get('enableSmartcardAssociations'),
        linkedAdults: LinkingSmartcardsStore.getAdultsLinkedSmartcardsForFare(orderFare.tickets.map(ticket => ticket.id)),
        linkedChildren: LinkingSmartcardsStore.getChildrenLinkedSmartcardsForFare(orderFare.tickets.map(ticket => ticket.id)),
        promoCode: PreviousSearchesStore.get('point').getPromoCode(),
        pendingPassengerAssistRequests: PassengerAssistStore.getPendingPassengerAssistRequests(),
    };
};

class Fare extends React.PureComponent {

    static propTypes = {
        orderFare: PropTypes.instanceOf(OrderFare).isRequired,
        open: PropTypes.bool,
        linkedAdults: PropTypes.instanceOf(List),
        linkedChildren: PropTypes.instanceOf(List),
        isFareOnly: PropTypes.bool,
        isAmended: PropTypes.bool,
        isSmartcard: PropTypes.bool,
        matchingTimetableAlert: PropTypes.object,
        isLoggedIn: PropTypes.bool,
        promoCode: PropTypes.string,
        orderTrip: PropTypes.instanceOf(OrderTrip),
        idx: PropTypes.number,
    };

    static defaultProps = {
        open: false,
        isFareOnly: false,
        isAmended: false,
        isSmartcard: false,
        matchingTimetableAlert: null,
        isLoggedIn: false,
    };

    _renderFareValidityDates () {
        const { orderFare } = this.props;
        return (
            <div className="fare__details text-sm">
                <h5 className="text-weight--normal fare__details__header">{ t('booking.fares.details.dates') }</h5>
                <ul className="fare__valid-dates">
                    { orderFare.outwardJourney && <Validity type={ 'outward' }
                                                            from={ orderFare.outwardJourney.validFrom }
                                                            to={ orderFare.outwardJourney.validTo }/> }
                    { orderFare.returnJourney && <Validity type={ 'return' }
                                                           from={ orderFare.returnJourney.validFrom }
                                                           to={ orderFare.returnJourney.validTo } /> }
                </ul>
            </div>
        );
    }

    render () {
        const {
            open,
            orderFare,
            isFareOnly,
            isAmended,
            linkedAdults,
            linkedChildren,
            isSmartcard,
            matchingTimetableAlert,
            isLoggedIn,
            promoCode,
            orderTrip,
            idx,
        } = this.props;
        const tickets = getFoldedTickets(orderFare.tickets);

        const hasRailcards = orderFare.getRailcards().some((railcard) => !railcard.isGroupSave());
        const showRailcardsMessage = hasRailcards && !isAmended;

        let alertsText = '';
        const prefs = [];

        if (matchingTimetableAlert) {
            const alertProps = matchingTimetableAlert.toJS();

            const {
                delaysChecked,
                stationAlertsChecked,
                timetableChangesChecked
            } = alertProps;


            if (isLoggedIn) {
                if (delaysChecked) {
                    prefs.push('disruption');
                }
                if (stationAlertsChecked) {
                    prefs.push('station facility');
                }
                if (timetableChangesChecked) {
                    prefs.push('timetable');
                }
            } else {
                prefs.push('timetable');
            }

            const prefsString = prefs.join(', ').replace(/,(?=[^,]*$)/, ' and');

            alertsText = `You will receive ${prefsString} alerts if there any changes to this journey.`;
        }

        const rewardPoints = tickets.reduce((sum, ticket) => sum + ticket.rewardPoints, 0);

        return (
            <div>
                <FareSummary { ...this.props } />
                { open && <div className="fare-details also-visible-print">
                    {isSmartcard ? (
                        <React.Fragment>
                            {tickets.some(ticket => ticket.adults > 0) && (
                                <LinkedSmartcards
                                    linkedPassengers={linkedAdults}
                                    tickets={tickets}
                                    showRailcardsMessage={showRailcardsMessage}
                                />
                            )}
                            {tickets.some(ticket => ticket.children > 0) && (
                                <LinkedSmartcards
                                    isChild
                                    linkedPassengers={linkedChildren}
                                    tickets={tickets}
                                    showRailcardsMessage={showRailcardsMessage}
                                />
                            )}
                        </React.Fragment>
                    ) : (
                        <FarePassengers orderFare={orderFare} isAmended={isAmended} promoCode={promoCode} />
                    )}
                    { !isFareOnly && !orderTrip.isTransPennineStaffRover() && <FareJourneys idx={idx} orderTrip={ orderTrip } orderFare={ orderFare } isAmended={ isAmended } /> }
                    { isFareOnly && this._renderFareValidityDates() }
                    { (matchingTimetableAlert && prefs.length > 0) && (<div>
                        <div>
                            <Icon style={ { marginTop: '3px', marginRight: '2px' } } icon="alert-active" className={'pull-left grid-basket-summary-v2__alert__icon'} />
                            <p><strong>{alertsText}</strong></p>
                        </div>
                    </div>)}
                    <ul className="list-unstyled exceptions-list padding-bottom--sm text-sm text-light">
                        <FareRestrictions fare={ orderFare } useBootstrap/>
                    </ul>
                </div> }
                <FeatureFlag name="enableRewardPoints">
                    <RewardPointsEarned rewardPoints={rewardPoints} className={"reward-points__earned pull-right"}/>
                </FeatureFlag>
            </div>
        );
    }
}

export default connectToStores(
    [FeatureFlagStore, LinkingSmartcardsStore, PreviousSearchesStore],
    getStateFromStores,
)(Fare);




// WEBPACK FOOTER //
// ./src/components/checkout/Fare.js